import { useState } from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { ContextDialogShell } from "./ContextDialogShell";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { userFacingErrorMessage } from '@/utils/userFacingError';

const MAX_RETRY_ATTEMPTS = 5; // Should match backend constant

interface DeclineContextModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestionId: string;
  retryAttempt: number;
  onDeclineComplete: () => void;
}

export function DeclineContextModal({
  open,
  onOpenChange,
  suggestionId,
  retryAttempt,
  onDeclineComplete,
}: DeclineContextModalProps) {
  const t = useTranslations("consultantReview.decline");
  const [declineContext, setDeclineContext] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bookkeepingSuggestionService = ServiceRegistry.getBookkeepingSuggestionService();
  const isAtMaxRetries = retryAttempt >= MAX_RETRY_ATTEMPTS;
  const submitLabel = t(isAtMaxRetries ? "sendForReview" : "submitRetry");

  const handleSubmit = async () => {
    if (!declineContext.trim()) {
      setError(t("contextRequired"));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isAtMaxRetries) {
        // Mark for manual review instead of retry
        await bookkeepingSuggestionService.markForManualReview(
          suggestionId,
          declineContext
        );
      } else {
        // Decline and queue retry
        await bookkeepingSuggestionService.declineAndRetry(
          suggestionId,
          declineContext
        );
      }

      setDeclineContext("");
      onDeclineComplete();
      onOpenChange(false);
    } catch (err) {
      console.error("Error declining suggestion:", err);
      setError(userFacingErrorMessage(err, t("failed")));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setDeclineContext("");
    setError(null);
    onOpenChange(false);
  };

  return (
    <ContextDialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={isAtMaxRetries ? t("maxTitle") : t("retryTitle", { attempt: String(retryAttempt + 1), max: String(MAX_RETRY_ATTEMPTS) })}
      description={isAtMaxRetries ? t("maxDescription") : t("retryDescription")}
      placeholder={t("placeholder")}
      value={declineContext}
      onValueChange={(value) => {
        setDeclineContext(value);
        setError(null);
      }}
      error={error}
      note={isAtMaxRetries ? undefined : t("note")}
      footer={
        <>
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!declineContext.trim() || isSubmitting}>
            {isSubmitting ? t("submitting") : submitLabel}
          </Button>
        </>
      }
    />
  );
}
